import type { ReactNode } from "react"

import type { Document } from "@/api/models"

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { parseCitationParts } from "@/utils/citation-utils"

import { CitationPeekContent } from "./citation-peek-content"

type CitationHoverCardProps = {
  citation: string
  citationSection: string
  title: string
  pageNumber: number | null
  chunkText?: string | null
  documentsList?: [string, Document][]
  onViewDocument: (
    citation: string,
    citationSection: string,
    options?: { chunkText?: string; documents?: [string, Document][] }
  ) => void
  children: ReactNode
  disabled?: boolean
}

/**
 * Hover card that shows a sneak peek of citation content on hover.
 * "View in document" opens the side panel via onViewDocument.
 */
export function CitationHoverCard({
  citation,
  citationSection,
  title,
  pageNumber,
  chunkText,
  documentsList,
  onViewDocument,
  children,
  disabled = false,
}: CitationHoverCardProps) {
  const { quote } = parseCitationParts(citation)

  if (disabled) {
    return <>{children}</>
  }

  return (
    <HoverCard closeDelay={150} openDelay={400}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align="start" className="w-80">
        <CitationPeekContent
          chunkText={chunkText}
          pageNumber={pageNumber}
          quoteText={quote}
          title={title}
          onViewDocument={() => {
            onViewDocument(citation, citationSection, {
              chunkText: chunkText ?? undefined,
              ...(documentsList ? { documents: documentsList } : {}),
            })
          }}
        />
      </HoverCardContent>
    </HoverCard>
  )
}
